import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase/client';
import type { Session, User } from '@supabase/supabase-js';

interface UseSupabaseReturn {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  client: typeof supabase;
}

export const useSupabase = (): UseSupabaseReturn => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Obtener sesión inicial
    const getSession = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        console.error('Error obteniendo sesión:', error.message);
      }
      setSession(session);
      setIsLoading(false);
    };

    getSession();

    // Escuchar cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setIsLoading(false);

        // Log de eventos de auth para debugging
        if (process.env.NODE_ENV === 'development') {
          console.log('Auth state change:', event);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    setIsLoading(true);
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error cerrando sesión:', error.message);
    }
    setIsLoading(false);
  };

  return {
    session,
    user: session?.user ?? null,
    isLoading,
    isAuthenticated: !!session,
    signOut,
    client: supabase
  };
};

// Hook para manejo de errores de Supabase
export const useSupabaseError = () => {
  const handleError = (error: any, context?: string) => {
    const errorMessage = error?.message || 'Error desconocido';
    const fullMessage = context ? `${context}: ${errorMessage}` : errorMessage;
    
    console.error('Supabase Error:', {
      context,
      error: errorMessage,
      details: error
    });

    // En producción podrías enviar esto a un servicio de logging
    return fullMessage;
  };

  return { handleError };
};

// Hook para estado de carga de operaciones
export const useSupabaseOperation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const executeOperation = async <T>(
    operation: () => Promise<T>,
    errorContext?: string
  ): Promise<T | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await operation();
      return result;
    } catch (err: any) {
      const errorMessage = err?.message || 'Error en la operación';
      const fullMessage = errorContext ? `${errorContext}: ${errorMessage}` : errorMessage;
      setError(fullMessage);
      console.error('Operation error:', err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const clearError = () => setError(null);

  return {
    isLoading,
    error,
    executeOperation,
    clearError
  };
};